// src/pages/EditBus.jsx
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../api";
import SeatLayoutSelector from "../components/SeatLayoutSelector";
import PointManager from "../components/PointManager";
import PriceMatrix from "../components/PriceMatrix";
import RotatedPointManager from "../components/RotatedPointManager";

// ─── helpers (logic unchanged) ──────────────────────────────────────────────
const cleanPoints = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map(({ time, point }) => ({
      time: String(time || "").trim(),
      point: String(point || "").trim(),
    }))
    .filter((r) => r.time && r.point);

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const parseCommaList = (val) =>
  String(val || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const normalizeWhatsappNumber = (val = "") =>
  String(val).replace(/\D/g, "").trim();

const cleanAuthorizedWhatsappNumbers = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((entry) => ({
      number: normalizeWhatsappNumber(entry?.number || ""),
      role: String(entry?.role || "operator").trim() || "operator",
      name: String(entry?.name || "").trim(),
    }))
    .filter((entry) => entry.number);

// ─── design tokens (identical to AddBus) ─────────────────────────────────────
const LABEL =
  "block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5";
const INPUT =
  "w-full bg-slate-50 border border-slate-200 text-slate-800 px-3.5 py-2.5 rounded-lg text-sm " +
  "placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 " +
  "transition-all duration-150";
const SELECT = INPUT;
const CARD = "bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden";
const CARD_HEAD =
  "flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white";
const CARD_BODY = "px-6 py-5";
const SECTION_ICON =
  "flex items-center justify-center w-8 h-8 rounded-lg text-white text-sm font-bold shrink-0";
const BADGE =
  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold";

// ─── tiny icon helpers ────────────────────────────────────────────────────────
const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ICONS = {
  bus:    "M8 6v6m8-6v6M3 10h18M5 18v2m14-2v2M3 6a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z",
  route:  "M3 12h18M12 3l9 9-9 9",
  seat:   "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  price:  "M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  star:   "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z",
  media:  "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z",
  tools:  "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  car:    "M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z M13 16H7v-3l2-3h7l2 3v3h-5z",
  phone:  "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  rotate: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  file:   "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  check:  "M5 13l4 4L19 7",
  x:      "M6 18L18 6M6 6l12 12",
  pencil: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
};

// ─── sub-components (identical to AddBus) ────────────────────────────────────
const SectionCard = ({ icon, iconBg, title, subtitle, children, extra }) => (
  <section className={CARD}>
    <div className={CARD_HEAD}>
      <span className={`${SECTION_ICON} ${iconBg}`}>
        <Icon d={ICONS[icon]} size={15} />
      </span>
      <div className="flex-1">
        <h2 className="text-sm font-bold text-slate-700 leading-tight">{title}</h2>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {extra}
    </div>
    <div className={CARD_BODY}>{children}</div>
  </section>
);

const Field = ({ label, hint, children, span }) => (
  <div className={span}>
    <label className={LABEL}>{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{hint}</p>}
  </div>
);

const Toggle = ({ checked, onChange, label, desc }) => (
  <label className="flex items-start gap-3 cursor-pointer group">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 ${
        checked ? "bg-sky-500" : "bg-slate-200"
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
    <span>
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      {desc && <span className="block text-xs text-slate-400 mt-0.5">{desc}</span>}
    </span>
  </label>
);

const FacilityChip = ({ name, label, checked, onChange }) => (
  <label
    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm font-medium transition-all duration-150 select-none ${
      checked
        ? "bg-sky-50 border-sky-400 text-sky-700"
        : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300"
    }`}
  >
    <input type="checkbox" name={name} checked={checked} onChange={onChange} className="sr-only" />
    <span className={`w-2 h-2 rounded-full shrink-0 ${checked ? "bg-sky-500" : "bg-slate-300"}`} />
    {label}
  </label>
);

const InfoBox = ({ color, children }) => {
  const colors = {
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue:  "bg-sky-50 border-sky-200 text-sky-800",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm leading-relaxed ${colors[color]}`}>
      {children}
    </div>
  );
};

const FileUpload = ({ label, accept, multiple, onChange, uploading, preview, onRemove }) => (
  <div>
    {label && <label className={LABEL}>{label}</label>}
    <div
      className={`relative mt-1 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 text-center transition-colors duration-150 ${
        uploading ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-slate-50 hover:border-sky-300 hover:bg-sky-50/40"
      }`}
    >
      <svg className="w-7 h-7 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={ICONS.media} />
      </svg>
      <span className="text-xs text-slate-500">
        {uploading ? "Uploading…" : "Click to browse or drag & drop"}
      </span>
      <span className="text-xs text-slate-400">Max 1.5 MB</span>
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </div>
    {preview && (
      <div className="mt-3 flex items-start gap-3">
        <img src={preview} alt="preview" className="h-20 rounded-lg border border-slate-200 object-cover" />
        {onRemove && (
          <button type="button" onClick={onRemove}
            className="flex items-center gap-1 text-xs text-rose-500 hover:text-rose-700 font-medium mt-1">
            <Icon d={ICONS.x} size={12} /> Remove
          </button>
        )}
      </div>
    )}
  </div>
);

// ─── main component ───────────────────────────────────────────────────────────
const EditBus = () => {
  const { busId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    from: "",
    to: "",
    date: "",
    departureTime: "",
    arrivalTime: "",
    listingMode: "booking",
    showPublicContact: false,
    busType: "AC",
    seatLayout: "",
    price: "",
    operatorLogo: "",
    unavailableDates: "",
    isAvailable: true,
    operator: "",
    features: { wifi: false, chargingPort: false },
    trendingOffer: { isActive: false, discountPercent: 0, message: "", expiry: "", imageUrl: "" },
    convenienceFee: { amountType: "fixed", value: 0 },
    rotationSchedule: { isRotating: false, startDate: "", rotationLength: "", intervals: [] },
    conductor: { name: "", mobile: "", altMobile: "" },
    owner: { name: "" },
    ownerNotifyMobile: "",
    authorizedWhatsappNumbers: [
      { number: "", role: "owner", name: "" },
      { number: "", role: "conductor", name: "" },
      { number: "", role: "assistant", name: "" },
    ],
    media: { cover: { url: "", publicId: "" }, gallery: [] },
    routeMeta: { via: [], distanceKm: "", durationMin: "" },
    facilities: {
      ac: false, recliner: false, tv: false, music: false, blanket: false,
      water: false, restroom: false, gps: false, liveTracking: false,
      luggage: false, usb: false, readingLight: false, airSuspension: false,
      extraTags: [],
    },
    vehicle: { registrationNo: "", year: "", make: "", model: "", seatCount: "" },
    details: "",
    detailsHtml: "",
    tags: [],
  });

  const [boardingPoints, setBoardingPoints] = useState([]);
  const [droppingPoints, setDroppingPoints] = useState([]);
  const [fares, setFares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operators, setOperators] = useState([]);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingOfferImage, setIsUploadingOfferImage] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);

  useEffect(() => {
    const fetchBus = async () => {
      try {
        const res = await apiClient.get(`/buses/${busId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const bus = res.data || {};

        const formattedDate = bus.date ? String(bus.date).split("T")[0] : "";
        const formattedExpiry = bus.trendingOffer?.expiry
          ? String(bus.trendingOffer.expiry).split("T")[0]
          : "";
        const formattedRotationStart = bus.rotationSchedule?.startDate
          ? String(bus.rotationSchedule.startDate).split("T")[0]
          : "";

        setForm((prev) => ({
          ...prev,
          ...bus,
          date: formattedDate,
          listingMode: bus.listingMode === "timetableOnly" ? "timetableOnly" : "booking",
          showPublicContact: !!bus.showPublicContact,
          seatLayout: Array.isArray(bus.seatLayout)
            ? bus.seatLayout.join(", ")
            : bus.seatLayout || "",
          unavailableDates: Array.isArray(bus.unavailableDates)
            ? bus.unavailableDates.join(", ")
            : bus.unavailableDates || "",
          operatorLogo: bus.operatorLogo || "",
          operator: bus.operator?._id || bus.operator || "",
          features: bus.features || { wifi: false, chargingPort: false },
          trendingOffer: {
            isActive: bus.trendingOffer?.isActive ?? false,
            discountPercent: bus.trendingOffer?.discountPercent ?? 0,
            message: bus.trendingOffer?.message ?? "",
            expiry: formattedExpiry || "",
            imageUrl: bus.trendingOffer?.imageUrl || "",
          },
          convenienceFee: bus.convenienceFee || { amountType: "fixed", value: 0 },
          rotationSchedule: bus.rotationSchedule
            ? { ...bus.rotationSchedule, startDate: formattedRotationStart || "" }
            : { isRotating: false, startDate: "", rotationLength: "", intervals: [] },
          conductor: {
            name: bus.conductor?.name || "",
            mobile: bus.conductor?.mobile || "",
            altMobile: bus.conductor?.altMobile || "",
          },
          owner: { name: bus.owner?.name || "" },
          ownerNotifyMobile: bus.ownerNotifyMobile || "",
          authorizedWhatsappNumbers:
            Array.isArray(bus.authorizedWhatsappNumbers) && bus.authorizedWhatsappNumbers.length
              ? [
                  bus.authorizedWhatsappNumbers.find((x) => x?.role === "owner") || { number: "", role: "owner", name: bus.owner?.name || "" },
                  bus.authorizedWhatsappNumbers.find((x) => x?.role === "conductor") || { number: "", role: "conductor", name: bus.conductor?.name || "" },
                  bus.authorizedWhatsappNumbers.find((x) => x?.role === "assistant") || { number: "", role: "assistant", name: "" },
                ]
              : [
                  { number: "", role: "owner", name: bus.owner?.name || "" },
                  { number: "", role: "conductor", name: bus.conductor?.name || "" },
                  { number: "", role: "assistant", name: "" },
                ],
          media: {
            cover: bus.media?.cover || { url: "", publicId: "" },
            gallery: Array.isArray(bus.media?.gallery) ? bus.media.gallery : [],
          },
          routeMeta: {
            via: Array.isArray(bus.routeMeta?.via) ? bus.routeMeta.via : parseCommaList(bus.routeMeta?.via),
            distanceKm: bus.routeMeta?.distanceKm === undefined || bus.routeMeta?.distanceKm === null ? "" : String(bus.routeMeta.distanceKm),
            durationMin: bus.routeMeta?.durationMin === undefined || bus.routeMeta?.durationMin === null ? "" : String(bus.routeMeta.durationMin),
          },
          facilities: {
            ac: !!bus.facilities?.ac,
            recliner: !!bus.facilities?.recliner,
            tv: !!bus.facilities?.tv,
            music: !!bus.facilities?.music,
            blanket: !!bus.facilities?.blanket,
            water: !!bus.facilities?.water,
            restroom: !!bus.facilities?.restroom,
            gps: !!bus.facilities?.gps,
            liveTracking: !!bus.facilities?.liveTracking,
            luggage: !!bus.facilities?.luggage,
            usb: !!bus.facilities?.usb,
            readingLight: !!bus.facilities?.readingLight,
            airSuspension: !!bus.facilities?.airSuspension,
            extraTags: Array.isArray(bus.facilities?.extraTags) ? bus.facilities.extraTags : parseCommaList(bus.facilities?.extraTags),
          },
          vehicle: {
            registrationNo: bus.vehicle?.registrationNo || "",
            year: bus.vehicle?.year === undefined || bus.vehicle?.year === null ? "" : String(bus.vehicle.year),
            make: bus.vehicle?.make || "",
            model: bus.vehicle?.model || "",
            seatCount: bus.vehicle?.seatCount === undefined || bus.vehicle?.seatCount === null ? "" : String(bus.vehicle.seatCount),
          },
          details: bus.details || "",
          detailsHtml: bus.detailsHtml || "",
          tags: Array.isArray(bus.tags) ? bus.tags : parseCommaList(bus.tags),
        }));

        if (!bus.rotationSchedule?.isRotating) {
          setBoardingPoints(bus.boardingPoints?.length ? bus.boardingPoints : [{ time: "", point: "" }]);
          setDroppingPoints(bus.droppingPoints?.length ? bus.droppingPoints : [{ time: "", point: "" }]);
        }

        setFares(bus.fares || []);
      } catch (err) {
        setError("Failed to load bus details. Please try again.");
        console.error(err);
      }
    };

    const fetchOperators = async () => {
      try {
        const res = await apiClient.get("/admin/operators", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setOperators(res.data || []);
      } catch (err) {
        console.error("Failed to load operators:", err);
      }
    };

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBus(), fetchOperators()]);
      setLoading(false);
    };

    if (busId) loadData();
  }, [busId]);

  // ─── handlers (logic unchanged) ──────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCheckboxChange = (e, field) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [field]: { ...prev[field], [name]: checked } }));
  };

  const handleLayoutChange = (layoutString) => setForm({ ...form, seatLayout: layoutString });

  const handleConvenienceFeeChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      convenienceFee: { ...prev.convenienceFee, [name]: name === "value" ? Number(value) : value },
    }));
  };

  const handleTrendingOfferChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      trendingOffer: { ...prev.trendingOffer, [name]: type === "checkbox" ? checked : value },
    }));
  };

  const handleFacilitiesChange = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, facilities: { ...prev.facilities, [name]: checked } }));
  };

  const handleRotationChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      rotationSchedule: { ...prev.rotationSchedule, [name]: type === "checkbox" ? checked : value },
    }));
  };

  const getDaysForRotation = () => {
    const length = Number(form.rotationSchedule.rotationLength);
    return isNaN(length) || length <= 0 ? [] : [...Array(length).keys()];
  };

  const handleIntervalChange = (dayOffset, turnIndex, field, value) => {
    const intervals = [...(form.rotationSchedule.intervals || [])];
    let dayEntry = intervals.find((i) => i.dayOffset === dayOffset);
    if (!dayEntry) { dayEntry = { dayOffset, turns: [] }; intervals.push(dayEntry); }
    if (!dayEntry.turns) dayEntry.turns = [];
    if (!dayEntry.turns[turnIndex]) {
      dayEntry.turns[turnIndex] = { departureTime: "", arrivalTime: "", boardingPoints: [], droppingPoints: [] };
    }
    dayEntry.turns[turnIndex][field] = value;
    intervals.sort((a, b) => a.dayOffset - b.dayOffset);
    setForm((prev) => ({ ...prev, rotationSchedule: { ...prev.rotationSchedule, intervals } }));
  };

  const addDayTurn = (dayOffset) => {
    const turns = form.rotationSchedule.intervals.find((i) => i.dayOffset === dayOffset)?.turns || [];
    handleIntervalChange(dayOffset, turns.length, "departureTime", "");
  };

  const removeDayTurn = (dayOffset, turnIndex) => {
    const intervals = [...(form.rotationSchedule.intervals || [])];
    const dayEntry = intervals.find((i) => i.dayOffset === dayOffset);
    if (dayEntry && dayEntry.turns) {
      dayEntry.turns.splice(turnIndex, 1);
      if (dayEntry.turns.length === 0) {
        const dayEntryIndex = intervals.findIndex((i) => i.dayOffset === dayOffset);
        if (dayEntryIndex > -1) intervals.splice(dayEntryIndex, 1);
      }
    }
    setForm((prev) => ({ ...prev, rotationSchedule: { ...prev.rotationSchedule, intervals } }));
  };

  // ─── uploads (logic unchanged) ────────────────────────────────────────────
  const uploadToCloudinary = async (file, folder) => {
    const fd = new FormData();
    fd.append("image", file);
    const res = await apiClient.post(`/upload?folder=${encodeURIComponent(folder)}`, fd, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "multipart/form-data" },
    });
    return res.data;
  };

  const onPickCover = async (file) => {
    if (!file) return;
    try {
      setIsUploadingCover(true);
      const { imageUrl, publicId } = await uploadToCloudinary(file, "buses/covers");
      setForm((prev) => ({ ...prev, media: { ...prev.media, cover: { url: imageUrl, publicId } } }));
    } catch { alert("Cover upload failed."); }
    finally { setIsUploadingCover(false); }
  };

  const onPickGallery = async (files) => {
    if (!files?.length) return;
    try {
      setIsUploadingGallery(true);
      const next = [];
      for (const f of files) {
        const { imageUrl, publicId } = await uploadToCloudinary(f, "buses/gallery");
        next.push({ url: imageUrl, publicId, caption: "" });
      }
      setForm((prev) => ({ ...prev, media: { ...prev.media, gallery: [...(prev.media.gallery || []), ...next] } }));
    } catch { alert("Gallery upload failed."); }
    finally { setIsUploadingGallery(false); }
  };

  const removeGalleryItem = (idx) => {
    setForm((prev) => { const g = [...(prev.media.gallery || [])]; g.splice(idx, 1); return { ...prev, media: { ...prev.media, gallery: g } }; });
  };

  const onPickOperatorLogo = async (file) => {
    if (!file) return;
    try {
      setIsUploadingLogo(true);
      if (file.size > 1.5 * 1024 * 1024) { alert("Please choose an image smaller than 1.5 MB."); return; }
      const dataUrl = await readFileAsDataURL(file);
      setForm((prev) => ({ ...prev, operatorLogo: dataUrl }));
    } catch { alert("Failed to load image. Try a different file."); }
    finally { setIsUploadingLogo(false); }
  };

  const onPickOfferImage = async (file) => {
    if (!file) return;
    try {
      setIsUploadingOfferImage(true);
      if (file.size > 1.5 * 1024 * 1024) { alert("Please choose an image smaller than 1.5 MB."); return; }
      const dataUrl = await readFileAsDataURL(file);
      setForm((prev) => ({ ...prev, trendingOffer: { ...prev.trendingOffer, imageUrl: dataUrl } }));
    } catch { alert("Failed to load image. Try a different file."); }
    finally { setIsUploadingOfferImage(false); }
  };

  // ─── submit (logic unchanged) ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const seatArray = (form.seatLayout || "")
      .split(",").map((s) => s.trim().toUpperCase()).filter((s) => s);
    const unavailableArray = (form.unavailableDates || "")
      .split(",").map((d) => d.trim()).filter((d) => d);

    const payload = {
      ...form,
      listingMode: form.listingMode === "timetableOnly" ? "timetableOnly" : "booking",
      showPublicContact: Boolean(form.showPublicContact),
      authorizedWhatsappNumbers: cleanAuthorizedWhatsappNumbers(form.authorizedWhatsappNumbers),
      seatLayout: form.listingMode === "booking" ? seatArray : [],
      price: Number(form.price || 0),
      unavailableDates: unavailableArray,
      boardingPoints: form.listingMode === "booking" && !form.rotationSchedule.isRotating ? cleanPoints(boardingPoints) : [],
      droppingPoints: form.listingMode === "booking" && !form.rotationSchedule.isRotating ? cleanPoints(droppingPoints) : [],
      fares: form.listingMode === "booking" && !form.rotationSchedule.isRotating ? fares : [],
      rotationSchedule: {
        ...form.rotationSchedule,
        rotationLength: form.rotationSchedule.isRotating ? parseInt(form.rotationSchedule.rotationLength, 10) : undefined,
        intervals: form.rotationSchedule.isRotating
          ? (form.rotationSchedule.intervals || []).map((i) => ({
              ...i,
              dayOffset: parseInt(i.dayOffset, 10),
              turns: (i.turns || []).map((t) => ({
                departureTime: t.departureTime || "",
                arrivalTime: t.arrivalTime || "",
                boardingPoints: cleanPoints(t.boardingPoints),
                droppingPoints: cleanPoints(t.droppingPoints),
              })),
            }))
          : [],
      },
      routeMeta: {
        via: Array.isArray(form.routeMeta.via) ? form.routeMeta.via : parseCommaList(form.routeMeta.via),
        distanceKm: form.routeMeta.distanceKm === "" || form.routeMeta.distanceKm === null ? undefined : Number(form.routeMeta.distanceKm),
        durationMin: form.routeMeta.durationMin === "" || form.routeMeta.durationMin === null ? undefined : Number(form.routeMeta.durationMin),
      },
      facilities: {
        ...form.facilities,
        extraTags: Array.isArray(form.facilities.extraTags) ? form.facilities.extraTags : parseCommaList(form.facilities.extraTags),
      },
      vehicle: {
        registrationNo: form.vehicle.registrationNo,
        year: form.vehicle.year === "" || form.vehicle.year === null ? undefined : Number(form.vehicle.year),
        make: form.vehicle.make,
        model: form.vehicle.model,
        seatCount: form.vehicle.seatCount === "" || form.vehicle.seatCount === null ? undefined : Number(form.vehicle.seatCount),
      },
      tags: Array.isArray(form.tags) ? form.tags : parseCommaList(form.tags),
    };

    try {
      await apiClient.put(`/buses/${busId}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      alert("✅ Bus updated successfully!");
      navigate("/admin/buses");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update bus");
      console.error("Update failed:", err);
    }
  };

  const summary = useMemo(() => ({
    title: form.name || "Bus",
    route: form.from && form.to ? `${form.from} → ${form.to}` : "Set route",
    time: form.departureTime && form.arrivalTime ? `${form.departureTime} → ${form.arrivalTime}` : "Set time",
    type: form.busType,
    mode: form.listingMode === "booking" ? "Online Booking" : "Timetable Only",
    price: form.price ? `Rs. ${Number(form.price).toLocaleString()}` : "Price not set",
    available: form.isAvailable,
  }), [form]);

  // ─── loading / error states ───────────────────────────────────────────────
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Loading bus details…</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md text-center bg-white border border-rose-100 rounded-2xl p-8 shadow-sm">
          <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon d={ICONS.x} size={20} />
          </div>
          <h2 className="text-base font-bold text-slate-800 mb-2">Failed to load bus</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );

  const FACILITY_LABELS = {
    ac: "A/C", recliner: "Recliner", tv: "TV / Screen", music: "Music", blanket: "Blanket",
    water: "Drinking Water", restroom: "Restroom", gps: "GPS", liveTracking: "Live Tracking",
    luggage: "Luggage Storage", usb: "USB Charging", readingLight: "Reading Light", airSuspension: "Air Suspension",
  };

  return (
    <div className="min-h-screen bg-slate-50/70">
      {/* ── Top bar ── */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Icon d={ICONS.pencil} size={14} />
            </div>
            <div>
              <span className="text-xs text-slate-400 uppercase tracking-widest font-semibold">Fleet Management</span>
              <span className="text-slate-300 mx-2">·</span>
              <span className="text-sm font-bold text-slate-700">Edit Bus</span>
              {form.name && (
                <>
                  <span className="text-slate-300 mx-2">·</span>
                  <span className="text-sm text-slate-500">{form.name}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/admin/buses")}
              className="px-4 py-1.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              form="edit-bus-form"
              type="submit"
              className="px-4 py-1.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200"
            >
              Update Bus
            </button>
          </div>
        </div>
      </div>

      {/* ── Page content ── */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <form id="edit-bus-form" onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ════════ LEFT: main form ════════ */}
          <div className="lg:col-span-2 space-y-5">

            {/* 1 ─ Bus & Operator */}
            <SectionCard icon="bus" iconBg="bg-sky-600" title="Bus & Operator" subtitle="Basic identity and assignment">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Bus Name / Number" span="sm:col-span-1">
                  <input id="name" name="name" className={INPUT} placeholder="e.g. SuperLine Express 07"
                    value={form.name} onChange={handleChange} required />
                </Field>
                <Field label="Assign Operator" span="sm:col-span-1">
                  <select id="operator" name="operator" className={SELECT} value={form.operator} onChange={handleChange} required>
                    <option value="">-- Select Operator --</option>
                    {operators.map((op) => (
                      <option key={op._id} value={op._id}>{op.fullName || op.email}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Listing Mode" hint="Timetable Only buses appear in search but disable seat booking." span="sm:col-span-1">
                  <select id="listingMode" name="listingMode" className={SELECT} value={form.listingMode} onChange={handleChange}>
                    <option value="booking">Online Booking Enabled</option>
                    <option value="timetableOnly">Timetable Only</option>
                  </select>
                </Field>
                <Field label="Bus Type" span="sm:col-span-1">
                  <select id="busType" name="busType" className={SELECT} value={form.busType} onChange={handleChange}>
                    <option value="AC">AC</option>
                    <option value="Non-AC">Non-AC</option>
                    <option value="Sleeper">Sleeper</option>
                  </select>
                </Field>

                {/* Operator Logo */}
                <div className="sm:col-span-2 space-y-3">
                  <div>
                    <label className={LABEL}>Operator Logo</label>
                    <input name="operatorLogo" className={INPUT} placeholder="https://…/logo.png  (or upload below)"
                      value={form.operatorLogo} onChange={handleChange} />
                  </div>
                  <FileUpload
                    accept="image/*"
                    onChange={(e) => onPickOperatorLogo(e.target.files?.[0])}
                    uploading={isUploadingLogo}
                    preview={form.operatorLogo}
                    onRemove={() => setForm((prev) => ({ ...prev, operatorLogo: "" }))}
                  />
                </div>
              </div>

              {/* Seat Layout */}
              <div className="mt-5">
                {form.listingMode === "booking" ? (
                  <>
                    <label className={LABEL}>Seat Layout</label>
                    <SeatLayoutSelector selectedLayout={form.seatLayout} onLayoutChange={handleLayoutChange} />
                    <p className="text-xs text-slate-400 mt-2">Tip: Paste comma-separated seats (e.g. A1, A2, B1…)</p>
                  </>
                ) : (
                  <InfoBox color="amber">
                    This bus is set to <strong>Timetable Only</strong>. Seat layout and fare matrix are hidden for this mode — you can still keep a display price and seat count.
                  </InfoBox>
                )}
              </div>
            </SectionCard>

            {/* 2 ─ Route & Schedule */}
            <SectionCard icon="route" iconBg="bg-indigo-600" title="Route & Schedule" subtitle="Journey path, timing and stops">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="From" span="sm:col-span-1">
                  <input id="from" name="from" className={INPUT} placeholder="Colombo"
                    value={form.from} onChange={handleChange} required />
                </Field>
                <Field label="To" span="sm:col-span-1">
                  <input id="to" name="to" className={INPUT} placeholder="Kandy"
                    value={form.to} onChange={handleChange} required />
                </Field>
                <Field label="Date of Journey" span="sm:col-span-1">
                  <input id="date" name="date" type="date" className={INPUT}
                    value={form.date} onChange={handleChange} required />
                </Field>
                <Field label="Departure Time" span="sm:col-span-1">
                  <input id="departureTime" name="departureTime" type="time" className={INPUT}
                    value={form.departureTime} onChange={handleChange} required />
                </Field>
                <Field label="Arrival Time" span="sm:col-span-1">
                  <input id="arrivalTime" name="arrivalTime" type="time" className={INPUT}
                    value={form.arrivalTime} onChange={handleChange} required />
                </Field>
                <Field label="Unavailable Dates" hint="Comma-separated YYYY-MM-DD dates. These dates will be blocked." span="sm:col-span-1">
                  <textarea id="unavailableDates" name="unavailableDates" rows={2} className={INPUT}
                    placeholder="2025-12-25, 2026-01-01"
                    value={form.unavailableDates} onChange={handleChange} />
                </Field>

                <Field label="Via (stops)" hint="Comma-separated intermediate stops." span="sm:col-span-3">
                  <input className={INPUT} placeholder="Matara, Galle, Kalutara"
                    value={Array.isArray(form.routeMeta.via) ? form.routeMeta.via.join(", ") : form.routeMeta.via}
                    onChange={(e) => setForm((prev) => ({ ...prev, routeMeta: { ...prev.routeMeta, via: e.target.value } }))} />
                </Field>
                <Field label="Distance (km)" span="sm:col-span-1">
                  <input type="number" min="0" className={INPUT} placeholder="e.g. 180"
                    value={form.routeMeta.distanceKm}
                    onChange={(e) => setForm((prev) => ({ ...prev, routeMeta: { ...prev.routeMeta, distanceKm: e.target.value } }))} />
                </Field>
                <Field label="Duration (min)" span="sm:col-span-1">
                  <input type="number" min="0" className={INPUT} placeholder="e.g. 240"
                    value={form.routeMeta.durationMin}
                    onChange={(e) => setForm((prev) => ({ ...prev, routeMeta: { ...prev.routeMeta, durationMin: e.target.value } }))} />
                </Field>
              </div>
            </SectionCard>

            {/* 3 ─ Boarding & Dropping Points */}
            {form.listingMode === "booking" && !form.rotationSchedule.isRotating && (
              <SectionCard icon="route" iconBg="bg-violet-600" title="Boarding & Dropping Points" subtitle="Stop-level pickup and drop-off">
                <div className="space-y-6">
                  <PointManager points={boardingPoints} setPoints={setBoardingPoints} pointType="Boarding" />
                  <hr className="border-slate-100" />
                  <PointManager points={droppingPoints} setPoints={setDroppingPoints} pointType="Dropping" />
                </div>
              </SectionCard>
            )}

            {/* 4 ─ Fare Matrix */}
            {form.listingMode === "booking" && !form.rotationSchedule.isRotating && (
              <SectionCard icon="price" iconBg="bg-emerald-600" title="Fares Between Points" subtitle="Point-to-point pricing matrix">
                <PriceMatrix boardingPoints={boardingPoints} droppingPoints={droppingPoints} fares={fares} setFares={setFares} />
              </SectionCard>
            )}

            {/* 5 ─ Pricing */}
            <SectionCard icon="price" iconBg="bg-emerald-600" title="Pricing" subtitle="Ticket prices and convenience fees">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label={form.listingMode === "booking" ? "Default Ticket Price (Rs.)" : "Display Price (Rs.)"} span="sm:col-span-1">
                  <input id="price" name="price" type="number" className={INPUT} placeholder="1500"
                    value={form.price} onChange={handleChange} required />
                </Field>
                {form.listingMode === "booking" && (
                  <>
                    <Field label="Fee Type" span="sm:col-span-1">
                      <select name="amountType" value={form.convenienceFee.amountType} onChange={handleConvenienceFeeChange} className={SELECT}>
                        <option value="fixed">Fixed (LKR)</option>
                        <option value="percentage">Percentage (%)</option>
                      </select>
                    </Field>
                    <Field label="Fee Value" span="sm:col-span-1">
                      <input type="number" name="value" min="0" step="0.01" className={INPUT} placeholder="0"
                        value={form.convenienceFee.value} onChange={handleConvenienceFeeChange} />
                    </Field>
                  </>
                )}
              </div>
              {form.listingMode === "booking" ? (
                <p className="text-xs text-slate-400 mt-3">Convenience fee is added on top of ticket price at checkout.</p>
              ) : (
                <div className="mt-3">
                  <InfoBox color="blue">
                    This bus is in <strong>Timetable Only</strong> mode. This price will be shown to passengers for reference, but direct booking and fare matrix are disabled.
                  </InfoBox>
                </div>
              )}
            </SectionCard>

            {/* 6 ─ Availability, Features & Offers */}
            <SectionCard icon="star" iconBg="bg-amber-500" title="Availability, Features & Offers" subtitle="Status flags, amenities and promotions">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Left column */}
                <div className="space-y-5">
                  <Toggle
                    checked={form.isAvailable}
                    onChange={(v) => setForm((p) => ({ ...p, isAvailable: v }))}
                    label="Bus is currently available"
                    desc="Toggle off to hide this bus from passenger search results."
                  />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Quick Features</p>
                    <div className="flex flex-wrap gap-2">
                      <FacilityChip name="wifi" label="Wi-Fi" checked={form.features.wifi}
                        onChange={(e) => handleCheckboxChange(e, "features")} />
                      <FacilityChip name="chargingPort" label="Charging Port" checked={form.features.chargingPort}
                        onChange={(e) => handleCheckboxChange(e, "features")} />
                    </div>
                  </div>
                </div>

                {/* Right column: Promotional Offer */}
                <div className="space-y-4">
                  <Toggle
                    checked={form.trendingOffer.isActive}
                    onChange={(v) => setForm((prev) => ({ ...prev, trendingOffer: { ...prev.trendingOffer, isActive: v } }))}
                    label="Enable Promotional Offer"
                    desc="Show a discount badge on this bus listing."
                  />
                  {form.trendingOffer.isActive && (
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Discount (%)">
                          <input id="discountPercent" name="discountPercent" type="number" className={INPUT}
                            value={form.trendingOffer.discountPercent} onChange={handleTrendingOfferChange} />
                        </Field>
                        <Field label="Expiry Date">
                          <input id="expiry" name="expiry" type="date" className={INPUT}
                            value={form.trendingOffer.expiry} onChange={handleTrendingOfferChange} />
                        </Field>
                      </div>
                      <Field label="Offer Message">
                        <input id="message" name="message" className={INPUT} placeholder="Get 10% off for early booking"
                          value={form.trendingOffer.message} onChange={handleTrendingOfferChange} />
                      </Field>
                      <div>
                        <label className={LABEL}>Offer Image</label>
                        <input id="imageUrl" name="imageUrl" className={`${INPUT} mb-2`} placeholder="https://…/offer.png (or upload below)"
                          value={form.trendingOffer.imageUrl} onChange={handleTrendingOfferChange} />
                        <FileUpload
                          accept="image/*"
                          onChange={(e) => onPickOfferImage(e.target.files?.[0])}
                          uploading={isUploadingOfferImage}
                          preview={form.trendingOffer.imageUrl}
                          onRemove={() => setForm((prev) => ({ ...prev, trendingOffer: { ...prev.trendingOffer, imageUrl: "" } }))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>

            {/* 7 ─ Media */}
            <SectionCard icon="media" iconBg="bg-pink-600" title="Media" subtitle="Cover photo and gallery images (Cloudinary)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <FileUpload
                    label="Cover Photo"
                    accept="image/*"
                    onChange={(e) => onPickCover(e.target.files?.[0])}
                    uploading={isUploadingCover}
                    preview={form.media?.cover?.url}
                    onRemove={() => setForm((prev) => ({ ...prev, media: { ...prev.media, cover: { url: "", publicId: "" } } }))}
                  />
                </div>
                <div>
                  <label className={LABEL}>Gallery Images</label>
                  <FileUpload
                    accept="image/*"
                    multiple
                    onChange={(e) => onPickGallery(e.target.files)}
                    uploading={isUploadingGallery}
                  />
                  {form.media?.gallery?.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {form.media.gallery.map((g, idx) => (
                        <div key={idx} className="relative group">
                          <img src={g.url} alt={`Gallery ${idx + 1}`}
                            className="h-20 w-full object-cover rounded-lg border border-slate-200" />
                          <button type="button" onClick={() => removeGalleryItem(idx)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-rose-200 text-rose-500 rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                            <Icon d={ICONS.x} size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>

            {/* 8 ─ Facilities */}
            <SectionCard icon="tools" iconBg="bg-teal-600" title="Facilities" subtitle="On-board amenities and comfort features">
              <div className="flex flex-wrap gap-2">
                {Object.keys(FACILITY_LABELS).map((key) => (
                  <FacilityChip key={key} name={key} label={FACILITY_LABELS[key]}
                    checked={!!form.facilities[key]} onChange={handleFacilitiesChange} />
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                <Field label="Extra Facility Tags" hint="Comma-separated (e.g. Ladies seat, 2+2 layout)">
                  <input className={INPUT} placeholder="Ladies seat, 2+2 layout"
                    value={Array.isArray(form.facilities.extraTags) ? form.facilities.extraTags.join(", ") : form.facilities.extraTags}
                    onChange={(e) => setForm((prev) => ({ ...prev, facilities: { ...prev.facilities, extraTags: e.target.value } }))} />
                </Field>
                <Field label="Public Search Tags" hint="Comma-separated (e.g. Express, Highway)">
                  <input className={INPUT} placeholder="Express, Highway"
                    value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
                    onChange={(e) => setForm((prev) => ({ ...prev, tags: e.target.value }))} />
                </Field>
              </div>
            </SectionCard>

            {/* 9 ─ Vehicle Details */}
            <SectionCard icon="car" iconBg="bg-slate-600" title="Vehicle Details" subtitle="Registration, make and capacity">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                <Field label="Registration No" span="sm:col-span-2">
                  <input className={INPUT} placeholder="ND-1234"
                    value={form.vehicle.registrationNo}
                    onChange={(e) => setForm((prev) => ({ ...prev, vehicle: { ...prev.vehicle, registrationNo: e.target.value } }))} />
                </Field>
                <Field label="Year" span="col-span-1">
                  <input type="number" className={INPUT} placeholder="2019"
                    value={form.vehicle.year}
                    onChange={(e) => setForm((prev) => ({ ...prev, vehicle: { ...prev.vehicle, year: e.target.value } }))} />
                </Field>
                <Field label="Make" span="col-span-1">
                  <input className={INPUT} placeholder="Ashok Leyland"
                    value={form.vehicle.make}
                    onChange={(e) => setForm((prev) => ({ ...prev, vehicle: { ...prev.vehicle, make: e.target.value } }))} />
                </Field>
                <Field label="Model" span="col-span-1">
                  <input className={INPUT} placeholder="Viking"
                    value={form.vehicle.model}
                    onChange={(e) => setForm((prev) => ({ ...prev, vehicle: { ...prev.vehicle, model: e.target.value } }))} />
                </Field>
                <Field label="Seat Count" hint="Shown on timetable-only cards." span="sm:col-span-2">
                  <input type="number" min="1" className={INPUT} placeholder="49"
                    value={form.vehicle.seatCount}
                    onChange={(e) => setForm((prev) => ({ ...prev, vehicle: { ...prev.vehicle, seatCount: e.target.value } }))} />
                </Field>
              </div>
            </SectionCard>

            {/* 10 ─ Extra Details */}
            <SectionCard icon="file" iconBg="bg-slate-500" title="Extra Details" subtitle="Freeform notes, policies or HTML content">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Details (plain / markdown)">
                  <textarea rows={5} className={INPUT}
                    placeholder="Write anything about this bus (policies, notes, highlights)…"
                    value={form.details}
                    onChange={(e) => setForm((prev) => ({ ...prev, details: e.target.value }))} />
                </Field>
                <Field label="Details HTML (optional)">
                  <textarea rows={5} className={INPUT}
                    placeholder={'<p>Rich content with <strong>HTML</strong>…</p>'}
                    value={form.detailsHtml}
                    onChange={(e) => setForm((prev) => ({ ...prev, detailsHtml: e.target.value }))} />
                </Field>
              </div>
            </SectionCard>

            {/* 11 ─ Rotating Schedule */}
            <SectionCard
              icon="rotate"
              iconBg="bg-orange-500"
              title="Rotating Schedule"
              subtitle="Multi-day repeating timetables"
              extra={
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.rotationSchedule.isRotating}
                    onClick={() => setForm((prev) => ({
                      ...prev,
                      rotationSchedule: { ...prev.rotationSchedule, isRotating: !prev.rotationSchedule.isRotating }
                    }))}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.rotationSchedule.isRotating ? "bg-orange-500" : "bg-slate-200"}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${form.rotationSchedule.isRotating ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                  <span className="text-slate-600 font-medium">{form.rotationSchedule.isRotating ? "Enabled" : "Disabled"}</span>
                </label>
              }
            >
              {form.rotationSchedule.isRotating ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                    <Field label="Start Date">
                      <input type="date" name="startDate" className={INPUT}
                        value={form.rotationSchedule.startDate || ""} onChange={handleRotationChange} required />
                    </Field>
                    <Field label="Rotation Length (days)">
                      <input type="number" name="rotationLength" className={INPUT} placeholder="e.g. 2 for a 2-day cycle"
                        value={form.rotationSchedule.rotationLength || ""} onChange={handleRotationChange} required />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {getDaysForRotation().map((dayOffset) => (
                      <div key={dayOffset} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                            Day {dayOffset + 1} of Rotation
                          </span>
                          <button type="button" onClick={() => addDayTurn(dayOffset)}
                            className="text-xs font-semibold text-sky-600 hover:text-sky-800">
                            + Add Turn for Day {dayOffset + 1}
                          </button>
                        </div>
                        {(form.rotationSchedule.intervals.find((i) => i.dayOffset === dayOffset)?.turns || []).map((turn, tIndex) => (
                          <div key={tIndex} className="rounded-lg border border-slate-200 bg-white p-3 mb-3 last:mb-0">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-semibold text-slate-600">Turn {tIndex + 1}</span>
                              <button type="button" onClick={() => removeDayTurn(dayOffset, tIndex)}
                                className="text-xs font-medium text-rose-500 hover:text-rose-700 border border-rose-200 px-2 py-0.5 rounded-md hover:bg-rose-50 transition-colors">
                                Remove Turn
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              <div>
                                <label className="text-xs text-slate-500 font-medium block mb-1">Departure</label>
                                <input type="time" value={turn.departureTime || ""} className={INPUT}
                                  onChange={(e) => handleIntervalChange(dayOffset, tIndex, "departureTime", e.target.value)} />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 font-medium block mb-1">Arrival</label>
                                <input type="time" value={turn.arrivalTime || ""} className={INPUT}
                                  onChange={(e) => handleIntervalChange(dayOffset, tIndex, "arrivalTime", e.target.value)} />
                              </div>
                            </div>
                            {/* Inline RotatedPointManager (EditBus renders per-turn, inline) */}
                            <div className="space-y-4">
                              <RotatedPointManager
                                label="Boarding Points"
                                points={turn.boardingPoints || []}
                                onChange={(updatedPoints) => handleIntervalChange(dayOffset, tIndex, "boardingPoints", updatedPoints)}
                              />
                              <RotatedPointManager
                                label="Dropping Points"
                                points={turn.droppingPoints || []}
                                onChange={(updatedPoints) => handleIntervalChange(dayOffset, tIndex, "droppingPoints", updatedPoints)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <InfoBox color="amber">Enable rotation to configure repeating multi-day schedules with per-turn boarding and dropping points.</InfoBox>
              )}
            </SectionCard>

            {/* 12 ─ Contacts */}
            <SectionCard icon="phone" iconBg="bg-rose-600" title="Contacts (Admin)" subtitle="Conductor, owner and WhatsApp access numbers">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Conductor Name">
                  <input className={INPUT} placeholder="e.g. Kamal Perera"
                    value={form.conductor.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, conductor: { ...prev.conductor, name: e.target.value } }))} />
                </Field>
                <Field label="Conductor Mobile">
                  <input className={INPUT} placeholder="0771234567"
                    value={form.conductor.mobile}
                    onChange={(e) => setForm((prev) => ({ ...prev, conductor: { ...prev.conductor, mobile: e.target.value } }))} />
                </Field>
                <Field label="Alt Mobile (optional)">
                  <input className={INPUT} placeholder="0719876543"
                    value={form.conductor.altMobile}
                    onChange={(e) => setForm((prev) => ({ ...prev, conductor: { ...prev.conductor, altMobile: e.target.value } }))} />
                </Field>
                <Field label="Owner Name">
                  <input className={INPUT} placeholder="Lakshan Transport (Pvt) Ltd"
                    value={form.owner.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, owner: { ...prev.owner, name: e.target.value } }))} />
                </Field>
                <Field label="Owner Notify Mobile">
                  <input className={INPUT} placeholder="0777654321"
                    value={form.ownerNotifyMobile}
                    onChange={(e) => setForm((prev) => ({ ...prev, ownerNotifyMobile: e.target.value }))} />
                </Field>
              </div>

              {/* WhatsApp Access */}
              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.136.559 4.133 1.538 5.86L0 24l6.306-1.508A11.935 11.935 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.005-1.365l-.36-.213-3.726.89.93-3.633-.234-.374A9.818 9.818 0 1112 21.818z" />
                  </svg>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-600">WhatsApp Booking Access</h3>
                </div>
                <p className="text-xs text-slate-400 mb-4">Numbers that receive booking summaries. Use format: 94771234567</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { role: "owner", roleLabel: "Owner / Operator", nameDefault: (prev) => prev.owner.name || "" },
                    { role: "conductor", roleLabel: "Conductor", nameDefault: (prev) => prev.conductor.name || "" },
                    { role: "assistant", roleLabel: "Assistant (optional)", nameDefault: () => "" },
                  ].map((item, i) => (
                    <div key={item.role} className="space-y-2">
                      <label className={LABEL}>{item.roleLabel} WhatsApp</label>
                      <input className={INPUT} placeholder="94771234567"
                        value={form.authorizedWhatsappNumbers?.[i]?.number || ""}
                        onChange={(e) => setForm((prev) => {
                          const next = [...(prev.authorizedWhatsappNumbers || [])];
                          next[i] = { ...(next[i] || {}), number: e.target.value, role: item.role, name: next[i]?.name || item.nameDefault(prev) };
                          return { ...prev, authorizedWhatsappNumbers: next };
                        })} />
                      <input className={INPUT} placeholder={`${item.roleLabel} name`}
                        value={form.authorizedWhatsappNumbers?.[i]?.name || ""}
                        onChange={(e) => setForm((prev) => {
                          const next = [...(prev.authorizedWhatsappNumbers || [])];
                          next[i] = { ...(next[i] || {}), name: e.target.value, role: item.role, number: next[i]?.number || "" };
                          return { ...prev, authorizedWhatsappNumbers: next };
                        })} />
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Toggle
                    checked={form.showPublicContact}
                    onChange={(v) => setForm((prev) => ({ ...prev, showPublicContact: v }))}
                    label="Show contact numbers on passenger-facing detail page"
                    desc="Enable only if you want passengers to see these contact numbers directly."
                  />
                </div>
                <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                  These contacts are stored with the bus and can be used later for SMS/alerts. WhatsApp numbers above are for booking-summary bot access for this specific bus.
                </p>
              </div>
            </SectionCard>

            {/* Bottom action row */}
            <div className="flex items-center justify-end gap-3 pb-8">
              <button type="button" onClick={() => navigate("/admin/buses")}
                className="px-5 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button type="submit"
                className="px-6 py-2.5 text-sm font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200 flex items-center gap-2">
                <Icon d={ICONS.check} size={15} />
                Update Bus
              </button>
            </div>
          </div>

          {/* ════════ RIGHT: sticky summary ════════ */}
          <aside className="lg:sticky lg:top-20 h-fit space-y-4">
            {/* Live summary card */}
            <div className={`${CARD} overflow-hidden`}>
              <div className="bg-gradient-to-br from-indigo-600 to-violet-600 px-5 py-5">
                <p className="text-indigo-200 text-xs font-semibold uppercase tracking-widest mb-1">Editing Bus</p>
                <h3 className="text-white text-lg font-bold leading-tight">{summary.title}</h3>
                <p className="text-indigo-200 text-sm mt-1">{summary.route}</p>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Type",  value: summary.type },
                    { label: "Time",  value: summary.time },
                    { label: "Price", value: summary.price },
                    { label: "Mode",  value: summary.mode },
                  ].map((item) => (
                    <div key={item.label} className="bg-slate-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-slate-400 font-medium">{item.label}</p>
                      <p className="text-sm font-semibold text-slate-700 mt-0.5 truncate">{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm font-medium text-slate-600">Availability</span>
                  <span className={`${BADGE} ${summary.available ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${summary.available ? "bg-emerald-500" : "bg-slate-400"}`} />
                    {summary.available ? "Active" : "Hidden"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Rotation</span>
                  <span className={`${BADGE} ${form.rotationSchedule.isRotating ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${form.rotationSchedule.isRotating ? "bg-orange-500" : "bg-slate-400"}`} />
                    {form.rotationSchedule.isRotating ? "Rotating" : "Fixed"}
                  </span>
                </div>
                {form.trendingOffer.isActive && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600">Promo</span>
                    <span className={`${BADGE} bg-amber-100 text-amber-700`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {form.trendingOffer.discountPercent}% OFF
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Tips */}
            <div className={CARD}>
              <div className={CARD_HEAD}>
                <span className={`${SECTION_ICON} bg-amber-400`}>
                  <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </span>
                <h3 className="text-sm font-bold text-slate-700">Quick Tips</h3>
              </div>
              <div className="px-5 py-4 space-y-2.5 text-xs text-slate-500 leading-relaxed">
                <p>• Use <strong className="text-slate-600">rotation</strong> when the bus operates on a repeating multi-day cycle.</p>
                <p>• Set <strong className="text-slate-600">point-to-point fares</strong> in the matrix for flexible pricing.</p>
                <p>• <strong className="text-slate-600">Unavailable dates</strong> block one-off holidays or maintenance windows.</p>
                <p>• <strong className="text-slate-600">Timetable Only</strong> lets a bus appear in search without direct seat booking.</p>
              </div>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default EditBus;

